import React, { useState, useEffect } from 'react';

const DictionaryAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [groqEnabled, setGroqEnabled] = useState(false);
  const [groqApiKeySet, setGroqApiKeySet] = useState(false);
  const [groqApiKeyMasked, setGroqApiKeyMasked] = useState('');
  const [groqApiKeyInput, setGroqApiKeyInput] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [groqSearchWord, setGroqSearchWord] = useState('');
  const [groqSearchResult, setGroqSearchResult] = useState(null);
  const [groqSearching, setGroqSearching] = useState(false);
  const [groqCachedWords, setGroqCachedWords] = useState([]);
  const [savedVocabulary, setSavedVocabulary] = useState([]);
  const [vocabSearchFilter, setVocabSearchFilter] = useState('');
  const [vocabPage, setVocabPage] = useState(1);
  const [vocabPerPage, setVocabPerPage] = useState(50);
  const [editingAiEntry, setEditingAiEntry] = useState(null);
  const [pydodaStats, setPydodaStats] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(null);
  const [deleteConfirmWord, setDeleteConfirmWord] = useState(null);
  const [selectedVocabWords, setSelectedVocabWords] = useState([]);
  const [bulkCategoryTarget, setBulkCategoryTarget] = useState('');
  const [bulkTypeTarget, setBulkTypeTarget] = useState('');
  const [showAddWordModal, setShowAddWordModal] = useState(false);
  const [newWord, setNewWord] = useState({
    darija: '', arabic: '', english: '', meaning: '', phonetic: '', wordType: '',
    grammarNote: '', conjugation: '', genderNote: '', culturalNote: '',
    examples: [], relatedWords: [], category: '', audioUrl: ''
  });
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadText, setBulkUploadText] = useState('');
  const [bulkUploadPreview, setBulkUploadPreview] = useState([]);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [editingVocabWord, setEditingVocabWord] = useState(null);
  const [dynamicWordTypes, setDynamicWordTypes] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingTypeName, setEditingTypeName] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState(null);
  const [confirmDeleteTypeOrCat, setConfirmDeleteTypeOrCat] = useState(null);
  const [dictionarySettings, setDictionarySettings] = useState({ is_visible: true, is_pro_only: false, hide_empty_categories: true, quick_suggestions_mode: 'custom', quick_suggestions_words: ['hello', 'goodbye', 'water', 'food', 'love', 'yes', 'no', 'please'], quick_suggestions_count: 8, search_min_length: 2, search_history_max: 8, search_history_display: 6, how_to_use_text: '', top_announcement: '' });
  const [dictAdminTab, setDictAdminTab] = useState('settings');

  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  // Load Dictionary Groq settings
  const loadDictGroqSettings = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/settings`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setGroqEnabled(data.enabled || false);
        setGroqApiKeySet(data.api_key_set || false);
        setGroqApiKeyMasked(data.api_key_masked || '');
      }
      // Also load PyDODa stats
      const statsRes = await fetch(`${API}/conversation-challenge/pydoda/stats`, { headers: authHeaders });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setPydodaStats(statsData);
      }
    } catch (error) {
      console.error('Failed to load Groq settings:', error);
    }
  };

  // Toggle Dictionary Groq AI
  const toggleDictGroqEnabled = async (enabled) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/settings`, {
        method: 'POST',
        headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ enabled: enabled })
      });
      if (response.ok) {
        setGroqEnabled(enabled);
        showNotification(enabled ? 'Groq AI Dictionary enabled' : 'Groq AI Dictionary disabled', 'success');
        // If enabling, reload cache and vocabulary
        if (enabled) {
          await loadDictGroqCache();
          await loadDictSavedVocabulary();
        }
      }
    } catch (error) {
      showNotification('Failed to update Groq settings', 'error');
    }
  };

  // Save Dictionary Groq API key
  const saveDictGroqApiKey = async () => {
    if (!groqApiKeyInput.trim()) {
      showNotification('Please enter an API key', 'error');
      return;
    }
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/settings`, {
        method: 'POST',
        headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ enabled: groqEnabled, api_key: groqApiKeyInput.trim() })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setGroqApiKeySet(true);
        setGroqApiKeyMasked(`***${groqApiKeyInput.slice(-4)}`);
        setGroqApiKeyInput('');
        setShowApiKeyInput(false);
        showNotification('Groq API key saved successfully', 'success');
        // Reload settings to refresh the UI
        await loadDictGroqSettings();
      } else {
        showNotification(data.error || 'Failed to save API key', 'error');
      }
    } catch (error) {
      console.error('Failed to save Groq settings');
      showNotification('Failed to save API key. Please try again.', 'error');
    }
  };

  // Clear Dictionary Groq API key
  const clearDictGroqApiKey = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/settings`, {
        method: 'POST',
        headers: {...authHeaders, 'Content-Type': 'application/json'},
        body: JSON.stringify({ enabled: groqEnabled, api_key: '' })
      });
      if (response.ok) {
        setGroqApiKeySet(false);
        setGroqApiKeyMasked('');
        showNotification('Groq API key cleared', 'success');
      }
    } catch (error) {
      showNotification('Failed to clear API key', 'error');
    }
  };

  // Search word using Dictionary Groq
  const searchDictGroqWord = async () => {
    if (!groqSearchWord.trim()) return;

    setGroqSearching(true);
    setGroqSearchResult(null);

    try {
      const response = await fetch(`${API}/conversation-challenge/groq/search/${encodeURIComponent(groqSearchWord.trim())}`, { headers: authHeaders });
      const data = await response.json();
      setGroqSearchResult(data);

      if (data.error) {
        showNotification(data.error, 'error');
      }
    } catch (error) {
      showNotification('Search failed', 'error');
    } finally {
      setGroqSearching(false);
    }
  };

  // Save Dictionary Groq result to vocabulary (with optional edits)
  const saveDictGroqToVocabulary = async (word, editedData = null) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/save/${encodeURIComponent(word)}`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: editedData ? JSON.stringify(editedData) : null
      });
      const data = await response.json();

      if (data.success) {
        showNotification(`"${word}" saved to your vocabulary!`, 'success');
        setEditingAiEntry(null);
        loadDictSavedVocabulary();
        loadDictGroqCache();
      } else {
        showNotification(data.error || 'Failed to save', 'error');
      }
    } catch (error) {
      showNotification('Failed to save word', 'error');
    }
  };

  // Edit AI-generated entry before saving
  const startEditingAiEntry = (word, data) => {
    setEditingAiEntry({
      word: word,
      data: {
        english: data.english || (Array.isArray(data.meanings) ? data.meanings.join(', ') : (data.meaning || '')),
        darija: data.darija || data.word || word,
        arabic: data.arabic || '',
        pronunciation: data.pronunciation || data.phonetic || '',
        type: data.type || data.wordType || '',
        category: data.category || data.pydoda_category || '',
        meaning: data.meaning || '',
        meanings: Array.isArray(data.meanings) ? data.meanings : (data.meaning ? [data.meaning] : []),
        grammar_note: data.grammar_note || data.grammarNote || '',
        conjugation: data.conjugation || '',
        gender_note: data.gender_note || data.genderNote || '',
        examples: data.examples || [],
        cultural_note: data.cultural_note || data.culturalNote || '',
        related_words: data.related_words || data.relatedWords || []
      }
    });
  };

  // Update editing AI entry field
  const updateEditingField = (field, value) => {
    setEditingAiEntry(prev => ({
      ...prev,
      data: { ...prev.data, [field]: value }
    }));
  };

  // Load Dictionary Groq cache
  const loadDictGroqCache = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/cache`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setGroqCachedWords(data.words || []);
      }
    } catch (error) {
      console.error('Failed to load Groq cache:', error);
    }
  };

  // Load Dictionary saved vocabulary
  const loadDictSavedVocabulary = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setSavedVocabulary(data.vocabulary || []);
      }
    } catch (error) {
      console.error('Failed to load vocabulary:', error);
    }
  };

  // --- Dynamic Word Types & Categories ---
  const loadDictWordTypes = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-types`);
      if (response.ok) {
        const data = await response.json();
        setDynamicWordTypes(data.word_types || []);
      }
    } catch (error) {
      console.error('Failed to load word types:', error);
    }
  };

  const loadDictWordCategories = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-categories`);
      if (response.ok) {
        const data = await response.json();
        setDynamicCategories(data.word_categories || []);
      }
    } catch (error) {
      console.error('Failed to load word categories:', error);
    }
  };

  const addWordType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-types`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name: newTypeName.trim() })
      });
      if (response.ok) {
        showNotification(`Added type "${newTypeName.trim()}"`, 'success');
        setNewTypeName('');
        loadDictWordTypes();
      } else {
        const err = await response.json();
        showNotification(err.detail || 'Failed to add', 'error');
      }
    } catch (error) {
      showNotification('Failed to add type', 'error');
    }
  };

  const renameWordType = async () => {
    if (!editingTypeName || !editingTypeName.newName.trim()) return;
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-types/rename`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ old_name: editingTypeName.oldName, new_name: editingTypeName.newName.trim() })
      });
      if (response.ok) {
        showNotification(`Renamed to "${editingTypeName.newName.trim()}"`, 'success');
        setEditingTypeName(null);
        loadDictWordTypes();
      } else {
        showNotification('Failed to rename', 'error');
      }
    } catch (error) {
      showNotification('Failed to rename type', 'error');
    }
  };

  const deleteWordType = async (name) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-types/delete`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name })
      });
      if (response.ok) {
        showNotification(`Deleted type "${name}"`, 'success');
        loadDictWordTypes();
      } else {
        showNotification('Failed to delete', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete type', 'error');
    }
  };

  const addWordCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-categories`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      if (response.ok) {
        showNotification(`Added category "${newCategoryName.trim()}"`, 'success');
        setNewCategoryName('');
        loadDictWordCategories();
      } else {
        const err = await response.json();
        showNotification(err.detail || 'Failed to add', 'error');
      }
    } catch (error) {
      showNotification('Failed to add category', 'error');
    }
  };

  const renameWordCategory = async () => {
    if (!editingCategoryName || !editingCategoryName.newName.trim()) return;
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-categories/rename`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ old_name: editingCategoryName.oldName, new_name: editingCategoryName.newName.trim() })
      });
      if (response.ok) {
        showNotification(`Renamed to "${editingCategoryName.newName.trim()}"`, 'success');
        setEditingCategoryName(null);
        loadDictWordCategories();
      } else {
        showNotification('Failed to rename', 'error');
      }
    } catch (error) {
      showNotification('Failed to rename category', 'error');
    }
  };

  const deleteWordCategory = async (name) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-categories/delete`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name })
      });
      if (response.ok) {
        showNotification(`Deleted category "${name}"`, 'success');
        loadDictWordCategories();
      } else {
        showNotification('Failed to delete', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete category', 'error');
    }
  };

  const moveWordType = async (name, direction) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-types/reorder`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name, direction })
      });
      if (response.ok) loadDictWordTypes();
    } catch (error) {
      showNotification('Failed to reorder', 'error');
    }
  };

  const moveWordCategory = async (name, direction) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/word-categories/reorder`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ name, direction })
      });
      if (response.ok) loadDictWordCategories();
    } catch (error) {
      showNotification('Failed to reorder', 'error');
    }
  };

  // Delete from Dictionary Groq cache
  const deleteDictFromGroqCache = async (word) => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/cache/${encodeURIComponent(word)}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (response.ok) {
        showNotification(`Deleted "${word}" from cache`, 'success');
        loadDictGroqCache();
      }
    } catch (error) {
      showNotification('Failed to delete', 'error');
    }
  };

  // Delete from Dictionary saved vocabulary
  const deleteDictFromVocabulary = async (word) => {
    if (!word) {
      showNotification('No word to delete', 'error');
      return;
    }

    try {
      // Use POST endpoint to handle special characters like /
      const response = await fetch(`${API}/conversation-challenge/vocabulary/delete`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ darija: word })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showNotification(`Deleted "${word}" from vocabulary`, 'success');
          setDeleteConfirmWord(null);
          loadDictSavedVocabulary();
        } else {
          showNotification(data.error || 'Word not found', 'error');
        }
      } else {
        showNotification('Failed to delete - server error', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Failed to delete: ' + error.message, 'error');
    }
  };

  // Delete ALL from Dictionary vocabulary
  const deleteDictAllVocabulary = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary/all`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (response.ok) {
        showNotification('All vocabulary deleted', 'success');
        loadDictSavedVocabulary();
        setConfirmDeleteAll(null);
        setSelectedVocabWords([]);
      }
    } catch (error) {
      showNotification('Failed to delete all', 'error');
    }
  };

  // Delete SELECTED Dictionary vocabulary words
  const deleteDictSelectedVocabulary = async () => {
    if (selectedVocabWords.length === 0) return;

    try {
      let successCount = 0;
      for (const word of selectedVocabWords) {
        const response = await fetch(`${API}/conversation-challenge/vocabulary/${encodeURIComponent(word)}`, {
          method: 'DELETE',
          headers: authHeaders
        });
        if (response.ok) successCount++;
      }

      showNotification(`Deleted ${successCount} of ${selectedVocabWords.length} selected words`, 'success');
      setSelectedVocabWords([]);
      loadDictSavedVocabulary();
    } catch (error) {
      showNotification('Failed to delete some words', 'error');
    }
  };

  // Bulk assign category to selected vocabulary words
  const bulkAssignCategory = async (category) => {
    if (selectedVocabWords.length === 0 || !category) return;
    try {
      let successCount = 0;
      for (const darija of selectedVocabWords) {
        const word = savedVocabulary.find(w => w.darija === darija);
        if (!word) continue;
        const response = await fetch(`${API}/conversation-challenge/vocabulary/update`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ _id: word._id, darija: word.darija, category })
        });
        if (response.ok) successCount++;
      }
      showNotification(`Assigned ${successCount} words to "${category}"`, 'success');
      setBulkCategoryTarget('');
      setSelectedVocabWords([]);
      loadDictSavedVocabulary();
    } catch (error) {
      showNotification('Failed to assign category', 'error');
    }
  };

  // Bulk assign word type to selected vocabulary words
  const bulkAssignType = async (wordType) => {
    if (selectedVocabWords.length === 0 || !wordType) return;
    try {
      let successCount = 0;
      for (const darija of selectedVocabWords) {
        const word = savedVocabulary.find(w => w.darija === darija);
        if (!word) continue;
        const response = await fetch(`${API}/conversation-challenge/vocabulary/update`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ _id: word._id, darija: word.darija, wordType })
        });
        if (response.ok) successCount++;
      }
      showNotification(`Assigned ${successCount} words to type "${wordType}"`, 'success');
      setBulkTypeTarget('');
      setSelectedVocabWords([]);
      loadDictSavedVocabulary();
    } catch (error) {
      showNotification('Failed to assign word type', 'error');
    }
  };

  // Delete ALL from Dictionary cache
  const deleteDictAllCache = async () => {
    try {
      const response = await fetch(`${API}/conversation-challenge/groq/cache/all`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (response.ok) {
        showNotification('All cache deleted', 'success');
        loadDictGroqCache();
        setConfirmDeleteAll(null);
      }
    } catch (error) {
      showNotification('Failed to delete all cache', 'error');
    }
  };

  // Add new word to Dictionary manually
  const addDictNewWord = async () => {
    if (!newWord.darija || !newWord.meaning) {
      showNotification('Darija and Meaning are required', 'error');
      return;
    }

    try {
      const response = await fetch(`${API}/conversation-challenge/vocabulary`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(newWord)
      });

      if (response.ok) {
        showNotification(`Added "${newWord.darija}" to vocabulary`, 'success');
        setShowAddWordModal(false);
        setNewWord({
          darija: '', arabic: '', english: '', meaning: '', phonetic: '', wordType: '',
          grammarNote: '', conjugation: '', genderNote: '', culturalNote: '',
          examples: [], relatedWords: [], category: '', audioUrl: ''
        });
        loadDictSavedVocabulary();
      } else {
        showNotification('Failed to add word', 'error');
      }
    } catch (error) {
      showNotification('Failed to add word', 'error');
    }
  };

  // Update existing Dictionary vocabulary word
  const updateDictVocabWord = async () => {
    if (!editingVocabWord) return;

    // Read latest HTML from contentEditable rich text editors
    const grammarEl = document.getElementById('edit-vocab-grammar-note');
    const culturalEl = document.getElementById('edit-vocab-cultural-note');
    const examplesEl = document.getElementById('edit-vocab-examples');
    const updatedWord = {
      ...editingVocabWord,
      grammarNote: grammarEl ? grammarEl.innerHTML : (editingVocabWord.grammarNote || ''),
      culturalNote: culturalEl ? culturalEl.innerHTML : (editingVocabWord.culturalNote || ''),
      examples: examplesEl ? examplesEl.innerHTML : (editingVocabWord.examples || ''),
    };

    try {
      // Use POST-based update to handle special chars (/ etc.) in darija
      const response = await fetch(`${API}/conversation-challenge/vocabulary/update`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedWord)
      });

      const data = await response.json();

      if (data.success) {
        showNotification(`Updated "${editingVocabWord.darija}"`, 'success');
        setEditingVocabWord(null);
        loadDictSavedVocabulary();
      } else {
        showNotification(data.error || 'Failed to update word', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Failed to update word: ' + error.message, 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadDictGroqSettings();
      await loadDictGroqCache();
      await loadDictSavedVocabulary();
      loadDictWordTypes();
      loadDictWordCategories();
      try {
        const dictSettingsRes = await fetch(`${API}/admin/dictionary/settings`, { headers: authHeaders });
        if (dictSettingsRes.ok) {
          const dictSettingsData = await dictSettingsRes.json();
          setDictionarySettings(prev => ({ ...prev, ...dictSettingsData, is_visible: dictSettingsData.is_visible !== false, is_pro_only: dictSettingsData.is_pro_only === true }));
        }
      } catch (e) { /* silent */ }
    };
    loadData();
  }, []);

  return (
    <div className="space-y-6">
      {/* Sub-Tab Buttons */}
      <div className="flex gap-2">
        {[
          { id: 'settings', label: 'Settings' },
          { id: 'ai_manager', label: 'AI Dictionary Manager' },
          { id: 'dictionary', label: 'Dictionary' },
          { id: 'backup', label: 'Backup' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setDictAdminTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${dictAdminTab === tab.id
              ? 'bg-cyan-600 text-white shadow-lg'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* SETTINGS TAB */}
      {dictAdminTab === 'settings' && (
        <div className="space-y-6">
          {/* Visibility & Access */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Visibility & Access
            </h4>
            <div className="space-y-4">
              {/* Visible Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Show in Menu</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show/hide Dictionary in sidebar navigation</p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !dictionarySettings.is_visible;
                    setDictionarySettings(prev => ({...prev, is_visible: newVal}));
                    try {
                      await fetch(`${API}/admin/dictionary/settings`, {
                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                        body: JSON.stringify({ is_visible: newVal })
                      });
                      showNotification(newVal ? 'Dictionary visible' : 'Dictionary hidden', 'success');
                    } catch (e) { showNotification('Failed to update', 'error'); }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${dictionarySettings.is_visible ? 'bg-green-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${dictionarySettings.is_visible ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {/* Pro Only Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                <div className="flex-1 mr-4">
                  <p className={`font-medium text-sm flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Premium Only</p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Require Pro subscription to use Dictionary</p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !dictionarySettings.is_pro_only;
                    setDictionarySettings(prev => ({...prev, is_pro_only: newVal}));
                    try {
                      await fetch(`${API}/admin/dictionary/settings`, {
                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                        body: JSON.stringify({ is_pro_only: newVal })
                      });
                      showNotification(newVal ? 'Dictionary: Pro only' : 'Dictionary: Free for all', 'success');
                    } catch (e) { showNotification('Failed to update', 'error'); }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${dictionarySettings.is_pro_only ? 'bg-purple-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${dictionarySettings.is_pro_only ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Top Announcement */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Top Announcement
            </h4>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This note will show at the top of the Dictionary page for all users. Leave empty to hide.</p>
            <textarea
              value={dictionarySettings.top_announcement || ''}
              onChange={(e) => setDictionarySettings(prev => ({...prev, top_announcement: e.target.value}))}
              placeholder="e.g., We just added 50 new words! Check them out..."
              rows={2}
              className={`w-full rounded-lg p-3 border text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
            />
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API}/admin/dictionary/settings`, {
                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                      body: JSON.stringify({ top_announcement: dictionarySettings.top_announcement || '' })
                    });
                    if (res.ok) {
                      showNotification('Announcement saved', 'success');
                    } else {
                      showNotification('Failed to save - server error', 'error');
                    }
                  } catch (e) { showNotification('Failed to save - network error', 'error'); }
                }}
                className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg"
              >
                Save Announcement
              </button>
              {dictionarySettings.top_announcement && (
                <button
                  onClick={async () => {
                    setDictionarySettings(prev => ({...prev, top_announcement: ''}));
                    try {
                      const res = await fetch(`${API}/admin/dictionary/settings`, {
                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                        body: JSON.stringify({ top_announcement: '' })
                      });
                      if (res.ok) showNotification('Announcement cleared', 'success');
                    } catch (e) {}
                  }}
                  className={`px-4 py-1.5 text-sm rounded-lg ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'}`}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Quick Suggestions */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Quick Suggestions
            </h4>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Words shown as clickable buttons before users search anything. Choose one mode:</p>

            {/* Mode Selection - radio style */}
            <div className="space-y-3 mb-4">
              {/* Custom Words Option */}
              <div
                onClick={async () => {
                  if (dictionarySettings.quick_suggestions_mode === 'custom') return;
                  setDictionarySettings(prev => ({...prev, quick_suggestions_mode: 'custom'}));
                  try {
                    const res = await fetch(`${API}/admin/dictionary/settings`, {
                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                      body: JSON.stringify({ quick_suggestions_mode: 'custom' })
                    });
                    if (res.ok) showNotification('Switched to Custom Words mode', 'success');
                  } catch (e) {}
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  dictionarySettings.quick_suggestions_mode === 'custom'
                    ? 'border-cyan-500 ' + (isDarkMode ? 'bg-cyan-900/30' : 'bg-cyan-50')
                    : (isDarkMode ? 'border-gray-600 bg-gray-800 hover:border-gray-500' : 'border-gray-200 bg-white hover:border-gray-300')
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    dictionarySettings.quick_suggestions_mode === 'custom' ? 'border-cyan-500' : isDarkMode ? 'border-gray-500' : 'border-gray-300'
                  }`}>
                    {dictionarySettings.quick_suggestions_mode === 'custom' && <div className="w-3 h-3 rounded-full bg-cyan-500" />}
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Custom Words</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>You choose which words to show as suggestions</p>
                  </div>
                  {dictionarySettings.quick_suggestions_mode === 'custom' && (
                    <span className="ml-auto text-xs px-2 py-0.5 bg-cyan-600 text-white rounded-full">Active</span>
                  )}
                </div>
              </div>

              {/* Random from Dictionary Option */}
              <div
                onClick={async () => {
                  if (dictionarySettings.quick_suggestions_mode === 'random') return;
                  setDictionarySettings(prev => ({...prev, quick_suggestions_mode: 'random'}));
                  try {
                    const res = await fetch(`${API}/admin/dictionary/settings`, {
                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                      body: JSON.stringify({ quick_suggestions_mode: 'random' })
                    });
                    if (res.ok) showNotification('Switched to Random mode - words picked from your vocabulary', 'success');
                  } catch (e) {}
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  dictionarySettings.quick_suggestions_mode === 'random'
                    ? 'border-cyan-500 ' + (isDarkMode ? 'bg-cyan-900/30' : 'bg-cyan-50')
                    : (isDarkMode ? 'border-gray-600 bg-gray-800 hover:border-gray-500' : 'border-gray-200 bg-white hover:border-gray-300')
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    dictionarySettings.quick_suggestions_mode === 'random' ? 'border-cyan-500' : isDarkMode ? 'border-gray-500' : 'border-gray-300'
                  }`}>
                    {dictionarySettings.quick_suggestions_mode === 'random' && <div className="w-3 h-3 rounded-full bg-cyan-500" />}
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Random from Dictionary</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Automatically picks random words from your vocabulary each time</p>
                  </div>
                  {dictionarySettings.quick_suggestions_mode === 'random' && (
                    <span className="ml-auto text-xs px-2 py-0.5 bg-cyan-600 text-white rounded-full">Active</span>
                  )}
                </div>
              </div>
            </div>

            {/* Custom Words Settings - only when custom is active */}
            {dictionarySettings.quick_suggestions_mode === 'custom' && (
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Your words ({(dictionarySettings.quick_suggestions_words || []).length}):
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(dictionarySettings.quick_suggestions_words || []).map((word, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                      {word}
                      <button onClick={() => {
                        setDictionarySettings(prev => ({
                          ...prev,
                          quick_suggestions_words: prev.quick_suggestions_words.filter((_, idx) => idx !== i)
                        }));
                      }} className="ml-1 text-red-400 hover:text-red-300 font-bold">x</button>
                    </span>
                  ))}
                  {(dictionarySettings.quick_suggestions_words || []).length === 0 && (
                    <span className={`text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No words added yet - add some below</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    id="dict-suggestion-input"
                    type="text"
                    placeholder="Type a word and click Add or press Enter..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const word = e.target.value.trim();
                        if (!(dictionarySettings.quick_suggestions_words || []).includes(word)) {
                          setDictionarySettings(prev => ({
                            ...prev,
                            quick_suggestions_words: [...(prev.quick_suggestions_words || []), word]
                          }));
                        } else {
                          showNotification('Word already exists', 'warning');
                        }
                        e.target.value = '';
                      }
                    }}
                    className={`flex-1 rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'}`}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('dict-suggestion-input');
                      if (input && input.value.trim()) {
                        const word = input.value.trim();
                        if (!(dictionarySettings.quick_suggestions_words || []).includes(word)) {
                          setDictionarySettings(prev => ({
                            ...prev,
                            quick_suggestions_words: [...(prev.quick_suggestions_words || []), word]
                          }));
                        } else {
                          showNotification('Word already exists', 'warning');
                        }
                        input.value = '';
                        input.focus();
                      }
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                  >
                    + Add
                  </button>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/dictionary/settings`, {
                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                        body: JSON.stringify({ quick_suggestions_words: dictionarySettings.quick_suggestions_words || [] })
                      });
                      if (res.ok) {
                        showNotification('Suggestions saved - will show on Dictionary page', 'success');
                      } else {
                        showNotification('Failed to save - server error', 'error');
                      }
                    } catch (e) { showNotification('Failed to save - network error', 'error'); }
                  }}
                  className="mt-3 px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg"
                >
                  Save Words
                </button>
              </div>
            )}

            {/* Random Mode Settings - only when random is active */}
            {dictionarySettings.quick_suggestions_mode === 'random' && (
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>How many random words to show:</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min={1} max={20}
                    value={dictionarySettings.quick_suggestions_count || 8}
                    onChange={(e) => setDictionarySettings(prev => ({...prev, quick_suggestions_count: parseInt(e.target.value) || 8}))}
                    className={`w-20 rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'}`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>words</span>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/admin/dictionary/settings`, {
                          method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                          body: JSON.stringify({ quick_suggestions_count: dictionarySettings.quick_suggestions_count || 8 })
                        });
                        if (res.ok) {
                          showNotification('Count saved', 'success');
                        } else {
                          showNotification('Failed to save - server error', 'error');
                        }
                      } catch (e) { showNotification('Failed to save - network error', 'error'); }
                    }}
                    className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg"
                  >
                    Save
                  </button>
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Each time a user opens the Dictionary, different random words from your vocabulary will appear.</p>
              </div>
            )}
          </div>

          {/* Search Limits */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Search Limits
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Min search length</label>
                <input type="number" min={1} max={10}
                  value={dictionarySettings.search_min_length || 2}
                  onChange={(e) => setDictionarySettings(prev => ({...prev, search_min_length: parseInt(e.target.value) || 2}))}
                  className={`w-full rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>History max stored</label>
                <input type="number" min={0} max={20}
                  value={dictionarySettings.search_history_max || 8}
                  onChange={(e) => setDictionarySettings(prev => ({...prev, search_history_max: parseInt(e.target.value) || 8}))}
                  className={`w-full rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>History max displayed</label>
                <input type="number" min={0} max={20}
                  value={dictionarySettings.search_history_display || 6}
                  onChange={(e) => setDictionarySettings(prev => ({...prev, search_history_display: parseInt(e.target.value) || 6}))}
                  className={`w-full rounded-lg p-2 border text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/dictionary/settings`, {
                    method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                    body: JSON.stringify({
                      search_min_length: dictionarySettings.search_min_length || 2,
                      search_history_max: dictionarySettings.search_history_max || 8,
                      search_history_display: dictionarySettings.search_history_display || 6
                    })
                  });
                  if (res.ok) {
                    showNotification('Search limits saved', 'success');
                  } else {
                    showNotification('Failed to save - server error', 'error');
                  }
                } catch (e) { showNotification('Failed to save - network error', 'error'); }
              }}
              className="mt-4 px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg"
            >
              Save Limits
            </button>
          </div>

          {/* How to Use Dictionary */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              How to Use Dictionary
            </h4>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This text will show at the bottom of the Dictionary page. Leave empty to hide.</p>
            <textarea
              value={dictionarySettings.how_to_use_text || ''}
              onChange={(e) => setDictionarySettings(prev => ({...prev, how_to_use_text: e.target.value}))}
              placeholder="e.g., Type any English or Darija word to search. Results include pronunciation, Arabic script, grammar notes, and cultural context."
              rows={4}
              className={`w-full rounded-lg p-3 border text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
            />
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API}/admin/dictionary/settings`, {
                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                      body: JSON.stringify({ how_to_use_text: dictionarySettings.how_to_use_text || '' })
                    });
                    if (res.ok) {
                      showNotification('How-to text saved', 'success');
                    } else {
                      showNotification('Failed to save - server error', 'error');
                    }
                  } catch (e) { showNotification('Failed to save - network error', 'error'); }
                }}
                className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg"
              >
                Save How-to Text
              </button>
              {dictionarySettings.how_to_use_text && (
                <button
                  onClick={async () => {
                    setDictionarySettings(prev => ({...prev, how_to_use_text: ''}));
                    try {
                      const res = await fetch(`${API}/admin/dictionary/settings`, {
                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                        body: JSON.stringify({ how_to_use_text: '' })
                      });
                      if (res.ok) showNotification('How-to text cleared', 'success');
                    } catch (e) {}
                  }}
                  className={`px-4 py-1.5 text-sm rounded-lg ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'}`}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI DICTIONARY MANAGER TAB */}
      {dictAdminTab === 'ai_manager' && (
        <div className="space-y-6">
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              AI Dictionary Manager
              <span className="text-xs px-2 py-0.5 bg-cyan-600 text-white rounded-full">Admin Only</span>
            </h3>
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage your vocabulary database. Search words, review AI-generated entries, and build your curated dictionary.
            </p>

            {/* Enable/Disable Groq Toggle */}
            <div className={`flex items-center justify-between p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gradient-to-r from-blue-900 to-cyan-900 border border-cyan-700' : 'bg-gradient-to-r from-blue-100 to-cyan-100 border border-cyan-300'}`}>
              <div>
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Groq AI Engine</span>
                <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Generate grammar for unknown words using AI</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleDictGroqEnabled(true)}
                  className={`px-4 py-2 rounded font-medium transition-all ${groqEnabled ? 'bg-green-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'}`}
                >
                  ON
                </button>
                <button
                  onClick={() => toggleDictGroqEnabled(false)}
                  className={`px-4 py-2 rounded font-medium transition-all ${!groqEnabled ? 'bg-red-600 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'}`}
                >
                  OFF
                </button>
              </div>
            </div>

            {/* Groq API Key Settings */}
            <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gradient-to-r from-orange-900/50 to-yellow-900/50 border border-orange-700/50' : 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300'}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Groq API Key</span>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Required for AI dictionary features</p>
                </div>
                <div className="flex items-center gap-2">
                  {groqApiKeySet ? (
                    <span className="text-green-500 text-sm flex items-center gap-1">
                      Key set {groqApiKeyMasked && `(${groqApiKeyMasked})`}
                    </span>
                  ) : (
                    <span className="text-yellow-500 text-sm">No API key configured</span>
                  )}
                </div>
              </div>

              {showApiKeyInput ? (
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={groqApiKeyInput}
                    onChange={(e) => setGroqApiKeyInput(e.target.value)}
                    placeholder="Enter your Groq API key (gsk_...)"
                    className={`flex-1 rounded px-3 py-2 text-sm border focus:outline-none ${isDarkMode ? 'bg-gray-800 text-white border-gray-600 focus:border-orange-500' : 'bg-white text-gray-800 border-gray-300 focus:border-orange-500'}`}
                  />
                  <button
                    onClick={saveDictGroqApiKey}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setShowApiKeyInput(false); setGroqApiKeyInput(''); }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowApiKeyInput(true)}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded font-medium text-sm"
                  >
                    {groqApiKeySet ? 'Change API Key' : 'Add API Key'}
                  </button>
                  {groqApiKeySet && (
                    <button
                      onClick={clearDictGroqApiKey}
                      className="px-4 py-2 bg-red-600/50 hover:bg-red-600 text-white rounded font-medium text-sm"
                    >
                      Remove Key
                    </button>
                  )}
                </div>
              )}

              <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Get your free API key from <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">console.groq.com/keys</a>
              </p>
            </div>

            {/* Always show these sections - PyDODa Stats, Search, AI Cache, Vocabulary */}
            <div className="space-y-6">
              {/* PyDODa Stats */}
              {pydodaStats && (
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-purple-900/50 border-purple-700' : 'bg-purple-50 border-purple-300'}`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>PyDODa Library Stats</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className={`rounded p-2 text-center ${isDarkMode ? 'bg-purple-800/50' : 'bg-purple-100'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-purple-200' : 'text-purple-700'}`}>{pydodaStats.total_darija_words?.toLocaleString()}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Darija Words</div>
                    </div>
                    <div className={`rounded p-2 text-center ${isDarkMode ? 'bg-purple-800/50' : 'bg-purple-100'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-purple-200' : 'text-purple-700'}`}>{pydodaStats.categories?.length}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Categories</div>
                    </div>
                    <div className={`rounded p-2 text-center ${isDarkMode ? 'bg-green-800/50' : 'bg-green-100'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>{savedVocabulary.length}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Your Vocabulary</div>
                    </div>
                    <div className={`rounded p-2 text-center ${isDarkMode ? 'bg-cyan-800/50' : 'bg-cyan-100'}`}>
                      <div className={`text-2xl font-bold ${isDarkMode ? 'text-cyan-200' : 'text-cyan-700'}`}>{groqCachedWords.length}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>AI Cache</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Box */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <label className={`font-medium block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Search & Generate Word</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={groqSearchWord}
                    onChange={(e) => setGroqSearchWord(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchDictGroqWord()}
                    placeholder="Enter a word (e.g., salam, klb, makla)..."
                    className={`flex-1 rounded-lg p-3 border focus:outline-none ${isDarkMode ? 'bg-gray-800 text-white border-gray-600 focus:border-cyan-500' : 'bg-white text-gray-800 border-gray-300 focus:border-cyan-500'}`}
                  />
                  <button
                    onClick={searchDictGroqWord}
                    disabled={groqSearching || !groqSearchWord.trim()}
                    className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 text-white rounded-lg font-medium"
                  >
                    {groqSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Search Result */}
              {groqSearchResult && (
                <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-gray-600' : 'bg-gradient-to-r from-gray-100 to-gray-50 border-gray-300'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <h4 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {groqSearchResult.pydoda_found ? 'PyDODa' : 'AI'} Result: "{groqSearchResult.word}"
                    </h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => groqSearchResult.data && startEditingAiEntry(groqSearchResult.word, groqSearchResult.data)}
                        className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => saveDictGroqToVocabulary(groqSearchResult.word)}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                      >
                        Save to Vocabulary
                      </button>
                    </div>
                  </div>
                  {groqSearchResult.pydoda_found && (
                    <div className={`mb-2 text-xs px-2 py-1 rounded inline-block ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                      Found in PyDODa library - AI enhanced
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Darija:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{groqSearchResult.data?.darija}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>English:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{groqSearchResult.data?.english}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Arabic:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{groqSearchResult.data?.arabic}</span>
                    </div>
                    <div>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Category:</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{groqSearchResult.data?.category}</span>
                    </div>
                  </div>
                  {groqSearchResult.data?.examples && groqSearchResult.data.examples.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <span className={`block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Examples:</span>
                      <div className="space-y-1">
                        {Array.isArray(groqSearchResult.data.examples) ? (
                          groqSearchResult.data.examples.map((ex, i) => (
                            <div key={i} className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                              {typeof ex === 'object' ? `${ex.darija} \u2192 ${ex.english}` : ex}
                            </div>
                          ))
                        ) : (
                          <span className={`${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{groqSearchResult.data.examples}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Cache Section */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>AI Cache ({groqCachedWords.length} words)</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={loadDictGroqCache}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                    >
                      Refresh
                    </button>
                    {groqCachedWords.length > 0 && (
                      <button
                        onClick={() => setConfirmDeleteAll('cache')}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                      >
                        Delete All Cache
                      </button>
                    )}
                  </div>
                </div>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Words generated by AI. Review, edit, then save to your vocabulary or delete.
                </p>
                <div className={`max-h-48 overflow-y-auto rounded-lg p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  {groqCachedWords.length === 0 ? (
                    <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No cached words. Search above to generate AI definitions.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {groqCachedWords.map((item, i) => (
                        <div key={i} className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${item.approved ? 'bg-green-700 text-white' : isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-800'} ${item.pydoda_found ? 'border border-purple-500' : ''}`}>
                          <span
                            className="cursor-pointer hover:text-cyan-300"
                            onClick={() => startEditingAiEntry(item.word, item.data || {})}
                          >
                            {item.word}
                          </span>
                          {item.pydoda_found && <span className="text-purple-400" title="From PyDODa">PyDODa</span>}
                          {item.approved && <span className="text-green-300" title="Saved">Saved</span>}
                          {!item.approved && (
                            <>
                              <button onClick={() => startEditingAiEntry(item.word, item.data || {})} className="text-yellow-400 hover:text-yellow-300" title="Edit">Edit</button>
                              <button onClick={() => saveDictGroqToVocabulary(item.word)} className="text-green-400 hover:text-green-300" title="Save">Save</button>
                              <button onClick={() => deleteDictFromGroqCache(item.word)} className="text-red-400 hover:text-red-300" title="Delete">x</button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
      {/* end AI Dictionary Manager */}

      {dictAdminTab === 'dictionary' && (
        <div className="space-y-6">

      {/* ═══ MANAGE WORD TYPES & CATEGORIES ═══ */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4`}>

        {/* Word Types Panel */}
        <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <h5 className={`font-medium text-sm mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
            Word Types ({dynamicWordTypes.length})
          </h5>
          <div className="flex gap-1 mb-2">
            <input type="text" value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addWordType()}
              placeholder="New type..."
              className={`flex-1 text-xs rounded p-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'}`}
            />
            <button onClick={addWordType}
              className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700">Add</button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto overflow-x-hidden pr-10">
            {dynamicWordTypes.map((t, i) => (
              <div key={t.name} className={`flex items-center text-xs p-1.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                {editingTypeName && editingTypeName.oldName === t.name ? (
                  <div className="flex gap-1 flex-1 min-w-0">
                    <input type="text" value={editingTypeName.newName}
                      onChange={(e) => setEditingTypeName({...editingTypeName, newName: e.target.value})}
                      onKeyDown={(e) => { if (e.key === 'Enter') renameWordType(); if (e.key === 'Escape') setEditingTypeName(null); }}
                      className={`flex-1 min-w-0 text-xs rounded p-1 border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-50 border-gray-300'}`}
                      autoFocus
                    />
                    <button onClick={renameWordType} className="text-green-500 hover:text-green-400 text-xs flex-shrink-0">OK</button>
                    <button onClick={() => setEditingTypeName(null)} className="text-gray-400 hover:text-gray-300 text-xs flex-shrink-0">X</button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col flex-shrink-0">
                      <button onClick={() => moveWordType(t.name, 'up')} disabled={i === 0}
                        className={`text-[10px] leading-none p-0.5 ${i === 0 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▲</button>
                      <button onClick={() => moveWordType(t.name, 'down')} disabled={i === dynamicWordTypes.length - 1}
                        className={`text-[10px] leading-none p-0.5 ${i === dynamicWordTypes.length - 1 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▼</button>
                    </div>
                    <span className={`truncate flex-1 min-w-0 ml-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{t.name}</span>
                    <div className="flex gap-1.5 flex-shrink-0 ml-2">
                      <button onClick={() => setEditingTypeName({ oldName: t.name, newName: t.name })}
                        className="text-yellow-500 hover:text-yellow-400 text-xs" title="Rename">✎</button>
                      <button onClick={() => setConfirmDeleteTypeOrCat({ kind: 'type', name: t.name })}
                        className="text-red-400 hover:text-red-300 text-xs" title="Delete">X</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Word Categories Panel */}
        <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <h5 className={`font-medium text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Categories ({dynamicCategories.length})
            </h5>
            <label className={`flex items-center gap-1.5 cursor-pointer text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Hide categories with 0 words from frontend">
              <span>Hide empty</span>
              <button
                onClick={async () => {
                  const newVal = !dictionarySettings.hide_empty_categories;
                  setDictionarySettings(prev => ({...prev, hide_empty_categories: newVal}));
                  try {
                    await fetch(`${API}/admin/dictionary/settings`, {
                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                      body: JSON.stringify({ hide_empty_categories: newVal })
                    });
                    showNotification(newVal ? 'Empty categories hidden from users' : 'All categories visible to users', 'success');
                  } catch (e) { showNotification('Failed to update', 'error'); }
                }}
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${dictionarySettings.hide_empty_categories ? 'bg-blue-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${dictionarySettings.hide_empty_categories ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </label>
          </div>
          <div className="flex gap-1 mb-2">
            <input type="text" value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addWordCategory()}
              placeholder="New category..."
              className={`flex-1 text-xs rounded p-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'}`}
            />
            <button onClick={addWordCategory}
              className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">Add</button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto overflow-x-hidden pr-10">
            {dynamicCategories.map((c, i) => (
              <div key={c.name} className={`flex items-center text-xs p-1.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                {editingCategoryName && editingCategoryName.oldName === c.name ? (
                  <div className="flex gap-1 flex-1 min-w-0">
                    <input type="text" value={editingCategoryName.newName}
                      onChange={(e) => setEditingCategoryName({...editingCategoryName, newName: e.target.value})}
                      onKeyDown={(e) => { if (e.key === 'Enter') renameWordCategory(); if (e.key === 'Escape') setEditingCategoryName(null); }}
                      className={`flex-1 min-w-0 text-xs rounded p-1 border ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-50 border-gray-300'}`}
                      autoFocus
                    />
                    <button onClick={renameWordCategory} className="text-green-500 hover:text-green-400 text-xs flex-shrink-0">OK</button>
                    <button onClick={() => setEditingCategoryName(null)} className="text-gray-400 hover:text-gray-300 text-xs flex-shrink-0">X</button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col flex-shrink-0">
                      <button onClick={() => moveWordCategory(c.name, 'up')} disabled={i === 0}
                        className={`text-[10px] leading-none p-0.5 ${i === 0 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▲</button>
                      <button onClick={() => moveWordCategory(c.name, 'down')} disabled={i === dynamicCategories.length - 1}
                        className={`text-[10px] leading-none p-0.5 ${i === dynamicCategories.length - 1 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▼</button>
                    </div>
                    <span className={`truncate flex-1 min-w-0 ml-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{c.name}</span>
                    <div className="flex gap-1.5 flex-shrink-0 ml-2">
                      <button onClick={() => setEditingCategoryName({ oldName: c.name, newName: c.name })}
                        className="text-yellow-500 hover:text-yellow-400 text-xs" title="Rename">✎</button>
                      <button onClick={() => setConfirmDeleteTypeOrCat({ kind: 'category', name: c.name })}
                        className="text-red-400 hover:text-red-300 text-xs" title="Delete">X</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gradient-to-r from-green-900 to-emerald-900 border-green-700' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'}`}>
            {/* Protection Notice */}
            <div className={`rounded-lg p-2 mb-3 flex items-center gap-2 ${isDarkMode ? 'bg-yellow-900/50 border border-yellow-600' : 'bg-yellow-50 border border-yellow-400'}`}>
              <span className="text-yellow-500 text-lg">🔒</span>
              <p className={`text-xs ${isDarkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
                <strong>PROTECTED DATA</strong> - This vocabulary is manually curated. Do not delete or modify programmatically.
              </p>
            </div>

            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>✅ Your Vocabulary ({savedVocabulary.length} {savedVocabulary.length === 1 ? 'word' : 'words'})</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddWordModal(true)}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                >
                  ➕ Add Word
                </button>
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                >
                  📤 Bulk Upload
                </button>
                <button
                  onClick={loadDictSavedVocabulary}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
                >
                  🔄 Refresh
                  </button>
                </div>
              </div>

              {/* Search Filter */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="🔍 Search vocabulary (Darija, English, Arabic...)"
                  value={vocabSearchFilter}
                  onChange={(e) => { setVocabSearchFilter(e.target.value); setVocabPage(1); }}
                  className={`w-full p-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'}`}
                />
              </div>

              {/* Pagination Controls */}
              {savedVocabulary.length > 0 && (() => {
                const filtered = savedVocabulary.filter(word => {
                  if (!vocabSearchFilter.trim()) return true;
                  const search = vocabSearchFilter.toLowerCase();
                  return (
                    (word.darija && word.darija.toLowerCase().includes(search)) ||
                    (word.english && word.english.toLowerCase().includes(search)) ||
                    (word.arabic && word.arabic.toLowerCase().includes(search)) ||
                    (word.category && word.category.toLowerCase().includes(search)) ||
                    (word.meaning && word.meaning.toLowerCase().includes(search))
                  );
                });
                const totalPages = Math.ceil(filtered.length / vocabPerPage);
                const startIdx = (vocabPage - 1) * vocabPerPage;
                const endIdx = Math.min(startIdx + vocabPerPage, filtered.length);
                return null; // Just for the IIFE pattern — actual controls below
              })()}
              <div className={`mb-3 flex flex-wrap items-center justify-between gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center gap-1">
                  <span className={`text-xs mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show:</span>
                  {[50, 100, 150].map(n => (
                    <button
                      key={n}
                      onClick={() => { setVocabPerPage(n); setVocabPage(1); }}
                      className={`px-2 py-1 rounded text-xs font-medium ${vocabPerPage === n ? 'bg-cyan-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-200'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {(() => {
                    const filtered = savedVocabulary.filter(word => {
                      if (!vocabSearchFilter.trim()) return true;
                      const search = vocabSearchFilter.toLowerCase();
                      return (word.darija && word.darija.toLowerCase().includes(search)) || (word.english && word.english.toLowerCase().includes(search)) || (word.arabic && word.arabic.toLowerCase().includes(search)) || (word.category && word.category.toLowerCase().includes(search)) || (word.meaning && word.meaning.toLowerCase().includes(search));
                    });
                    const start = Math.min((vocabPage - 1) * vocabPerPage + 1, filtered.length);
                    const end = Math.min(vocabPage * vocabPerPage, filtered.length);
                    return `${start}-${end} of ${filtered.length}`;
                  })()}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setVocabPage(p => Math.max(1, p - 1))}
                    disabled={vocabPage <= 1}
                    className={`px-2 py-1 rounded text-xs ${vocabPage <= 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-600'} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600 hover:bg-gray-200'}`}
                  >
                    Prev
                  </button>
                  <span className={`text-xs px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {vocabPage} / {(() => {
                      const filtered = savedVocabulary.filter(word => {
                        if (!vocabSearchFilter.trim()) return true;
                        const search = vocabSearchFilter.toLowerCase();
                        return (word.darija && word.darija.toLowerCase().includes(search)) || (word.english && word.english.toLowerCase().includes(search)) || (word.arabic && word.arabic.toLowerCase().includes(search)) || (word.category && word.category.toLowerCase().includes(search)) || (word.meaning && word.meaning.toLowerCase().includes(search));
                      });
                      return Math.max(1, Math.ceil(filtered.length / vocabPerPage));
                    })()}
                  </span>
                  <button
                    onClick={() => {
                      const filtered = savedVocabulary.filter(word => {
                        if (!vocabSearchFilter.trim()) return true;
                        const search = vocabSearchFilter.toLowerCase();
                        return (word.darija && word.darija.toLowerCase().includes(search)) || (word.english && word.english.toLowerCase().includes(search)) || (word.arabic && word.arabic.toLowerCase().includes(search)) || (word.category && word.category.toLowerCase().includes(search)) || (word.meaning && word.meaning.toLowerCase().includes(search));
                      });
                      const maxPage = Math.max(1, Math.ceil(filtered.length / vocabPerPage));
                      setVocabPage(p => Math.min(maxPage, p + 1));
                    }}
                    disabled={(() => {
                      const filtered = savedVocabulary.filter(word => {
                        if (!vocabSearchFilter.trim()) return true;
                        const search = vocabSearchFilter.toLowerCase();
                        return (word.darija && word.darija.toLowerCase().includes(search)) || (word.english && word.english.toLowerCase().includes(search)) || (word.arabic && word.arabic.toLowerCase().includes(search)) || (word.category && word.category.toLowerCase().includes(search)) || (word.meaning && word.meaning.toLowerCase().includes(search));
                      });
                      return vocabPage >= Math.max(1, Math.ceil(filtered.length / vocabPerPage));
                    })()}
                    className={`px-2 py-1 rounded text-xs ${(() => { const filtered = savedVocabulary.filter(word => { if (!vocabSearchFilter.trim()) return true; const search = vocabSearchFilter.toLowerCase(); return (word.darija && word.darija.toLowerCase().includes(search)) || (word.english && word.english.toLowerCase().includes(search)) || (word.arabic && word.arabic.toLowerCase().includes(search)) || (word.category && word.category.toLowerCase().includes(search)) || (word.meaning && word.meaning.toLowerCase().includes(search)); }); return vocabPage >= Math.max(1, Math.ceil(filtered.length / vocabPerPage)); })() ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-600'} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600 hover:bg-gray-200'}`}
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Selection Actions */}
              {selectedVocabWords.length > 0 && (
                <div className={`mb-3 p-2 rounded ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>{selectedVocabWords.length} words selected</span>
                    <div className="flex gap-2 flex-wrap">
                      <select
                        value={bulkCategoryTarget}
                        onChange={(e) => { setBulkCategoryTarget(e.target.value); if (e.target.value) bulkAssignCategory(e.target.value); }}
                        className={`px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                      >
                        <option value="">Assign Category...</option>
                        {dynamicCategories.map(c => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                      <select
                        value={bulkTypeTarget}
                        onChange={(e) => { setBulkTypeTarget(e.target.value); if (e.target.value) bulkAssignType(e.target.value); }}
                        className={`px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                      >
                        <option value="">Assign Type...</option>
                        {dynamicWordTypes.map(t => (
                          <option key={t.name} value={t.name}>{t.name.charAt(0).toUpperCase() + t.name.slice(1)}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setSelectedVocabWords([])}
                        className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setConfirmDeleteAll('selected')}
                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                      >
                        Delete Selected
                      </button>
                    </div>
                  </div>
                  <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-yellow-300/60' : 'text-yellow-600/60'}`}>Select words with checkboxes, then pick a category or type to bulk assign without editing each word.</p>
                </div>
              )}

              <div className={`rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                {savedVocabulary.length === 0 ? (
                  <p className={`text-sm text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No saved vocabulary yet. Click "Add Word" or enable Groq AI to search and generate words.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <tr>
                        <th className={`p-2 text-left w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <input
                            type="checkbox"
                            checked={selectedVocabWords.length === savedVocabulary.length && savedVocabulary.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVocabWords(savedVocabulary.map(w => w.darija));
                              } else {
                                setSelectedVocabWords([]);
                              }
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className={`p-2 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Darija</th>
                        <th className={`p-2 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>English</th>
                        <th className={`p-2 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category</th>
                        <th className={`p-2 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type</th>
                        <th className={`p-2 text-right ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedVocabulary
                        .filter(word => {
                          if (!vocabSearchFilter.trim()) return true;
                          const search = vocabSearchFilter.toLowerCase();
                          return (
                            (word.darija && word.darija.toLowerCase().includes(search)) ||
                            (word.english && word.english.toLowerCase().includes(search)) ||
                            (word.arabic && word.arabic.toLowerCase().includes(search)) ||
                            (word.category && word.category.toLowerCase().includes(search)) ||
                            (word.meaning && word.meaning.toLowerCase().includes(search))
                          );
                        })
                        .slice((vocabPage - 1) * vocabPerPage, vocabPage * vocabPerPage)
                        .map((word, i) => (
                        <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedVocabWords.includes(word.darija)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVocabWords([...selectedVocabWords, word.darija]);
                                } else {
                                  setSelectedVocabWords(selectedVocabWords.filter(w => w !== word.darija));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className={`p-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.darija}</td>
                          <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{word.english}</td>
                          <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{word.category}</td>
                          <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{word.type}</td>
                          <td className="p-2 text-right">
                            <button
                              onClick={() => setEditingVocabWord({
                                ...word,
                                originalDarija: word.darija,
                                type: word.type || '',
                                grammarNote: word.grammar_note || word.grammarNote || '',
                                culturalNote: word.cultural_note || word.culturalNote || '',
                                genderNote: word.gender_note || word.genderNote || '',
                                audioUrl: word.audio_url || word.audioUrl || '',
                                examples: word.examples || '',
                              })}
                              className="text-yellow-500 hover:text-yellow-400 mr-2"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => setDeleteConfirmWord(word)}
                              className="text-red-500 hover:text-red-400"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
      </div>
        </div>
      )}

      {/* ═══ BACKUP TAB ═══ */}
      {dictAdminTab === 'backup' && (
        <div className="space-y-6">
          <div className={`rounded-lg p-6 border ${isDarkMode ? 'bg-gradient-to-r from-indigo-900 to-purple-900 border-indigo-700' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-300'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>💾 Backup & Restore</h3>
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Download your vocabulary as JSON or TXT. Restore from a JSON backup anytime — it only adds/updates, never deletes.
            </p>

            {/* Download Section */}
            <div className={`rounded-lg p-4 mb-4 border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>📥 Download Backup</h4>
              <div className="flex flex-wrap gap-3">
                {/* JSON Download */}
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/conversation-challenge/vocabulary/export`, { headers: authHeaders });
                      if (!response.ok) throw new Error('Export failed');
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `dictionary_backup_${new Date().toISOString().split('T')[0]}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                      showNotification('JSON backup downloaded!', 'success');
                    } catch (err) {
                      showNotification('Backup failed: ' + err.message, 'error');
                    }
                  }}
                  className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm"
                >
                  📄 Download JSON
                </button>

                {/* TXT Download */}
                <button
                  onClick={() => {
                    try {
                      if (!savedVocabulary || savedVocabulary.length === 0) {
                        showNotification('No vocabulary to export', 'error');
                        return;
                      }
                      const lines = [`Dictionary Backup — ${new Date().toLocaleDateString()} — ${savedVocabulary.length} words\n${'='.repeat(60)}\n`];
                      const sorted = [...savedVocabulary].sort((a, b) => (a.darija || '').localeCompare(b.darija || ''));
                      sorted.forEach((w, i) => {
                        lines.push(`#${i + 1}`);
                        lines.push(`  Darija:        ${w.darija || ''}`);
                        lines.push(`  English:       ${w.english || ''}`);
                        if (w.arabic) lines.push(`  Arabic:        ${w.arabic}`);
                        if (w.pronunciation) lines.push(`  Pronunciation: ${w.pronunciation}`);
                        if (w.meaning) lines.push(`  Meaning:       ${w.meaning}`);
                        if (w.type) lines.push(`  Type:          ${w.type}`);
                        if (w.category) lines.push(`  Category:      ${w.category}`);
                        if (w.gender_note || w.genderNote) lines.push(`  Gender Note:   ${w.gender_note || w.genderNote}`);
                        if (w.grammar_note || w.grammarNote) lines.push(`  Grammar Note:  ${w.grammar_note || w.grammarNote}`);
                        if (w.cultural_note || w.culturalNote) lines.push(`  Cultural Note: ${w.cultural_note || w.culturalNote}`);
                        if (w.audio_url || w.audioUrl) lines.push(`  Audio URL:     ${w.audio_url || w.audioUrl}`);
                        lines.push('');
                      });
                      lines.push(`${'='.repeat(60)}`);
                      lines.push(`Total: ${savedVocabulary.length} words`);
                      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `dictionary_backup_${new Date().toISOString().split('T')[0]}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                      showNotification('TXT backup downloaded!', 'success');
                    } catch (err) {
                      showNotification('TXT export failed: ' + err.message, 'error');
                    }
                  }}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm"
                >
                  📝 Download TXT
                </button>
              </div>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                JSON = full backup (can be restored). TXT = readable numbered list for review.
              </p>
            </div>

            {/* Restore Section */}
            <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
              <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>📤 Restore from Backup</h4>
              <button
                onClick={() => {
                  const fileInput = document.createElement('input');
                  fileInput.type = 'file';
                  fileInput.accept = '.json';
                  fileInput.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const text = await file.text();
                      const data = JSON.parse(text);
                      if (!data.words || !Array.isArray(data.words)) {
                        showNotification('Invalid backup file — no "words" array found', 'error');
                        return;
                      }
                      if (!window.confirm(`Restore ${data.words.length} words from backup?\n\nThis will add/update words — nothing will be deleted.`)) return;
                      const response = await fetch(`${API}/conversation-challenge/vocabulary/import`, {
                        method: 'POST',
                        headers: { ...authHeaders, 'Content-Type': 'application/json' },
                        body: text
                      });
                      const result = await response.json();
                      if (result.success) {
                        showNotification(`Restored ${result.imported} words from backup!`, 'success');
                        loadDictSavedVocabulary();
                      } else {
                        showNotification(result.error || 'Restore failed', 'error');
                      }
                    } catch (err) {
                      showNotification('Restore failed: ' + err.message, 'error');
                    }
                  };
                  fileInput.click();
                }}
                className="px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium text-sm"
              >
                📂 Choose JSON File to Restore
              </button>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Only accepts JSON files from "Download JSON". Adds/updates words — never deletes existing data.
              </p>
            </div>

            {/* Quick Stats */}
            <div className={`mt-4 rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Current vocabulary: <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>{savedVocabulary.length} words</strong>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteAll && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 border ${isDarkMode ? 'bg-gray-800 border-red-600' : 'bg-white border-red-400'}`}>
            <h3 className="text-xl font-bold text-red-500 mb-4">⚠️ Confirm Delete</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {confirmDeleteAll === 'vocabulary' && `Are you sure you want to delete ALL ${savedVocabulary.length} words from your vocabulary? This cannot be undone!`}
              {confirmDeleteAll === 'cache' && `Are you sure you want to delete ALL ${groqCachedWords.length} words from AI cache?`}
              {confirmDeleteAll === 'selected' && `Are you sure you want to delete ${selectedVocabWords.length} selected words?`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteAll(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmDeleteAll === 'vocabulary') {
                    await deleteDictAllVocabulary();
                  } else if (confirmDeleteAll === 'cache') {
                    await deleteDictAllCache();
                  } else if (confirmDeleteAll === 'selected') {
                    await deleteDictSelectedVocabulary();
                  }
                  setConfirmDeleteAll(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                🗑️ Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Word Confirmation Modal */}
      {deleteConfirmWord && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 border ${isDarkMode ? 'bg-gray-800 border-red-600' : 'bg-white border-red-400'}`}>
            <h3 className="text-xl font-bold text-red-500 mb-4">🗑️ Delete Word</h3>
            <div className="mb-4">
              <p className={`mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Are you sure you want to delete this word?</p>
              <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'}`}>
                <div className="text-green-500 font-medium text-lg">{deleteConfirmWord.darija}</div>
                <div className={isDarkMode ? 'text-white' : 'text-gray-800'}>{deleteConfirmWord.english}</div>
                {deleteConfirmWord.arabic && <div className="text-blue-400 text-right" dir="rtl">{deleteConfirmWord.arabic}</div>}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmWord(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteDictFromVocabulary(deleteConfirmWord.darija)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Word Type / Category Confirmation Modal */}
      {confirmDeleteTypeOrCat && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 border ${isDarkMode ? 'bg-gray-800 border-red-600' : 'bg-white border-red-400'}`}>
            <h3 className="text-xl font-bold text-red-500 mb-4">Delete {confirmDeleteTypeOrCat.kind === 'type' ? 'Word Type' : 'Category'}</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to delete <strong>"{confirmDeleteTypeOrCat.name}"</strong>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteTypeOrCat(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmDeleteTypeOrCat.kind === 'type') {
                    await deleteWordType(confirmDeleteTypeOrCat.name);
                  } else {
                    await deleteWordCategory(confirmDeleteTypeOrCat.name);
                  }
                  setConfirmDeleteTypeOrCat(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Word Modal - Full Featured */}
      {showAddWordModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full mx-4 border my-auto ${isDarkMode ? 'bg-gray-800 border-green-600' : 'bg-white border-green-400'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-green-500">➕ Add New Word</h3>
              <button onClick={() => setShowAddWordModal(false)} className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>×</button>
            </div>

            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* English */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English *</label>
                <input
                  type="text"
                  value={newWord.english || ''}
                  onChange={(e) => setNewWord({...newWord, english: e.target.value})}
                  placeholder="e.g., hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Darija (Latin) */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija (Latin) *</label>
                <input
                  type="text"
                  value={newWord.darija || ''}
                  onChange={(e) => setNewWord({...newWord, darija: e.target.value})}
                  placeholder="e.g., salam"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Arabic Script */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script</label>
                <input
                  type="text"
                  value={newWord.arabic || ''}
                  onChange={(e) => setNewWord({...newWord, arabic: e.target.value})}
                  placeholder="سلام"
                  className={`w-full rounded-lg p-2.5 border text-right ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  dir="rtl"
                />
              </div>

              {/* Pronunciation */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pronunciation</label>
                <input
                  type="text"
                  value={newWord.pronunciation || ''}
                  onChange={(e) => setNewWord({...newWord, pronunciation: e.target.value})}
                  placeholder="e.g., sa-LAM"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Meaning/Definition */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Meaning/Definition</label>
                <input
                  type="text"
                  value={newWord.meaning || ''}
                  onChange={(e) => setNewWord({...newWord, meaning: e.target.value})}
                  placeholder="e.g., greeting used to say hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Word Type */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Word Type</label>
                <select
                  value={newWord.type || ''}
                  onChange={(e) => setNewWord({...newWord, type: e.target.value})}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select type...</option>
                  {dynamicWordTypes.map(t => (
                    <option key={t.name} value={t.name}>{t.name.charAt(0).toUpperCase() + t.name.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                <select
                  value={newWord.category || ''}
                  onChange={(e) => setNewWord({...newWord, category: e.target.value})}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select Category...</option>
                  {dynamicCategories.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Gender Note */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Gender Note</label>
                <input
                  type="text"
                  value={newWord.genderNote || ''}
                  onChange={(e) => setNewWord({...newWord, genderNote: e.target.value})}
                  placeholder="e.g., masculine form"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Audio URL */}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL (Dropbox link)</label>
                <input
                  type="text"
                  value={newWord.audioUrl || ''}
                  onChange={(e) => setNewWord({...newWord, audioUrl: e.target.value})}
                  placeholder="https://dl.dropboxusercontent.com/..."
                  className={`w-full rounded-lg p-2.5 border text-xs ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>

              {/* Grammar Note — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Grammar Note</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`w-7 h-7 rounded text-[10px] hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>S</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>M</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`w-7 h-7 rounded text-sm hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>L</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="new-vocab-grammar-note"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: newWord.grammarNote || ''}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setNewWord(prev => ({...prev, grammarNote: html})); }}
                    className={`w-full min-h-[60px] px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>

              {/* Cultural Note — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cultural Note</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`w-7 h-7 rounded text-[10px] hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>S</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>M</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`w-7 h-7 rounded text-sm hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>L</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="new-vocab-cultural-note"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: newWord.culturalNote || ''}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setNewWord(prev => ({...prev, culturalNote: html})); }}
                    className={`w-full min-h-[60px] px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>

              {/* Examples — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Examples</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="new-vocab-examples"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: newWord.examples || ''}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setNewWord(prev => ({...prev, examples: html})); }}
                    className={`w-full min-h-[60px] px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowAddWordModal(false);
                  setNewWord({});
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newWord.darija || !newWord.english) {
                    showNotification('English and Darija are required', 'error');
                    return;
                  }
                  // Read latest HTML from rich text editors
                  const gEl = document.getElementById('new-vocab-grammar-note');
                  const cEl = document.getElementById('new-vocab-cultural-note');
                  const eEl = document.getElementById('new-vocab-examples');
                  try {
                    const response = await fetch(`${API}/conversation-challenge/vocabulary`, {
                      method: 'POST',
                      headers: { ...authHeaders, 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        darija: newWord.darija,
                        english: newWord.english,
                        arabic: newWord.arabic || '',
                        meaning: newWord.meaning || '',
                        pronunciation: newWord.pronunciation || '',
                        type: newWord.type || '',
                        category: newWord.category || 'Other',
                        genderNote: newWord.genderNote || '',
                        grammarNote: gEl ? gEl.innerHTML : (newWord.grammarNote || ''),
                        culturalNote: cEl ? cEl.innerHTML : (newWord.culturalNote || ''),
                        audioUrl: newWord.audioUrl || '',
                        examples: eEl ? eEl.innerHTML : (newWord.examples || '')
                      })
                    });
                    if (response.ok) {
                      showNotification(`Added "${newWord.darija}" to vocabulary`, 'success');
                      setShowAddWordModal(false);
                      setNewWord({});
                      loadDictSavedVocabulary();
                    }
                  } catch (error) {
                    showNotification('Failed to add word', 'error');
                  }
                }}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
              >
                💾 Save Word
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vocabulary Word Modal */}
      {editingVocabWord && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full mx-4 border my-auto ${isDarkMode ? 'bg-gray-800 border-yellow-600' : 'bg-white border-yellow-400'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-yellow-500">✏️ Edit Vocabulary Word</h3>
              <button onClick={() => setEditingVocabWord(null)} className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>×</button>
            </div>

            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English *</label>
                <input
                  type="text"
                  value={editingVocabWord.english || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, english: e.target.value})}
                  placeholder="e.g., hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija (Latin) *</label>
                <input
                  type="text"
                  value={editingVocabWord.darija || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, darija: e.target.value})}
                  placeholder="e.g., salam"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script</label>
                <input
                  type="text"
                  value={editingVocabWord.arabic || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, arabic: e.target.value})}
                  placeholder="سلام"
                  className={`w-full rounded-lg p-2.5 border text-right ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  dir="rtl"
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pronunciation</label>
                <input
                  type="text"
                  value={editingVocabWord.pronunciation || editingVocabWord.phonetic || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, pronunciation: e.target.value})}
                  placeholder="e.g., sa-LAM"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Meaning/Definition</label>
                <input
                  type="text"
                  value={editingVocabWord.meaning || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, meaning: e.target.value})}
                  placeholder="e.g., greeting used to say hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Word Type</label>
                <select
                  value={editingVocabWord.type || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, type: e.target.value})}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select type...</option>
                  {dynamicWordTypes.map(t => (
                    <option key={t.name} value={t.name}>{t.name.charAt(0).toUpperCase() + t.name.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                <select
                  value={editingVocabWord.category || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, category: e.target.value})}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select Category...</option>
                  {dynamicCategories.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Gender Note</label>
                <input
                  type="text"
                  value={editingVocabWord.genderNote || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, genderNote: e.target.value})}
                  placeholder="e.g., masculine form"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                <input
                  type="text"
                  value={editingVocabWord.audioUrl || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, audioUrl: e.target.value})}
                  placeholder="https://..."
                  className={`w-full rounded-lg p-2.5 border text-xs ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              {/* Grammar Note — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Grammar Note</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`w-7 h-7 rounded text-[10px] hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>S</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>M</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`w-7 h-7 rounded text-sm hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>L</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="edit-vocab-grammar-note"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: editingVocabWord.grammarNote || ''}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setEditingVocabWord(prev => ({...prev, grammarNote: html})); }}
                    className={`w-full min-h-[60px] max-h-[300px] resize-y overflow-auto px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>

              {/* Cultural Note — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cultural Note</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '1'); }} className={`w-7 h-7 rounded text-[10px] hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>S</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '4'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>M</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('fontSize', false, '6'); }} className={`w-7 h-7 rounded text-sm hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>L</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="edit-vocab-cultural-note"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: editingVocabWord.culturalNote || ''}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setEditingVocabWord(prev => ({...prev, culturalNote: html})); }}
                    className={`w-full min-h-[60px] max-h-[300px] resize-y overflow-auto px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>

              {/* Examples — Rich Text */}
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Examples</label>
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className={`flex flex-wrap items-center gap-1 px-2 py-1 border-b ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-200'}`}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }} className={`w-7 h-7 rounded font-bold text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>B</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }} className={`w-7 h-7 rounded italic text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>I</button>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('underline'); }} className={`w-7 h-7 rounded underline text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>U</button>
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    {['#ef4444','#f97316','#22c55e','#3b82f6','#a855f7','#6b7280'].map(color => (
                      <button key={color} type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('foreColor', false, color); }} className="w-5 h-5 rounded-full border border-gray-400 hover:scale-110 transition-transform" style={{backgroundColor: color}} />
                    ))}
                    <span className={`mx-1 w-px h-5 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`}></span>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); document.execCommand('removeFormat'); }} className={`w-7 h-7 rounded text-xs hover:bg-opacity-80 ${isDarkMode ? 'hover:bg-gray-500 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}>✕</button>
                  </div>
                  <div
                    id="edit-vocab-examples"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{__html: typeof editingVocabWord.examples === 'string' ? editingVocabWord.examples : (Array.isArray(editingVocabWord.examples) ? editingVocabWord.examples.join('<br>') : '')}}
                    onBlur={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setEditingVocabWord(prev => ({...prev, examples: html})); }}
                    className={`w-full min-h-[60px] max-h-[300px] resize-y overflow-auto px-3 py-2 text-sm outline-none ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{lineHeight: '1.6'}}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => setEditingVocabWord(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={updateDictVocabWord}
                className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-medium"
              >
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit AI Entry Modal */}
      {editingAiEntry && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full mx-4 border my-auto ${isDarkMode ? 'bg-gray-800 border-cyan-600' : 'bg-white border-cyan-400'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-cyan-500">✏️ Edit AI Entry: "{editingAiEntry.word}"</h3>
              <button onClick={() => setEditingAiEntry(null)} className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>×</button>
            </div>

            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English *</label>
                <input
                  type="text"
                  value={editingAiEntry.data.english || (Array.isArray(editingAiEntry.data.meanings) ? editingAiEntry.data.meanings.join(', ') : '') || ''}
                  onChange={(e) => updateEditingField('english', e.target.value)}
                  placeholder="e.g., hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija (Latin) *</label>
                <input
                  type="text"
                  value={editingAiEntry.data.darija || ''}
                  onChange={(e) => updateEditingField('darija', e.target.value)}
                  placeholder="e.g., salam"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script</label>
                <input
                  type="text"
                  value={editingAiEntry.data.arabic || ''}
                  onChange={(e) => updateEditingField('arabic', e.target.value)}
                  placeholder="سلام"
                  className={`w-full rounded-lg p-2.5 border text-right ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                  dir="rtl"
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pronunciation</label>
                <input
                  type="text"
                  value={editingAiEntry.data.pronunciation || ''}
                  onChange={(e) => updateEditingField('pronunciation', e.target.value)}
                  placeholder="e.g., sa-LAM"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Meaning/Definition</label>
                <input
                  type="text"
                  value={editingAiEntry.data.meaning || ''}
                  onChange={(e) => updateEditingField('meaning', e.target.value)}
                  placeholder="e.g., greeting used to say hello"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Word Type</label>
                <select
                  value={editingAiEntry.data.type || ''}
                  onChange={(e) => updateEditingField('type', e.target.value)}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select type...</option>
                  {dynamicWordTypes.map(t => (
                    <option key={t.name} value={t.name}>{t.name.charAt(0).toUpperCase() + t.name.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                <select
                  value={editingAiEntry.data.category || ''}
                  onChange={(e) => updateEditingField('category', e.target.value)}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  <option value="">Select Category...</option>
                  {dynamicCategories.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Gender Note</label>
                <input
                  type="text"
                  value={editingAiEntry.data.gender_note || ''}
                  onChange={(e) => updateEditingField('gender_note', e.target.value)}
                  placeholder="e.g., masculine form"
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio URL</label>
                <input
                  type="text"
                  value={editingAiEntry.data.audioUrl || ''}
                  onChange={(e) => updateEditingField('audioUrl', e.target.value)}
                  placeholder="https://..."
                  className={`w-full rounded-lg p-2.5 border text-xs ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Grammar Note</label>
                <textarea
                  value={editingAiEntry.data.grammar_note || ''}
                  onChange={(e) => updateEditingField('grammar_note', e.target.value)}
                  placeholder="Grammar rules, usage notes, examples..."
                  rows={2}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
              <div className="col-span-2">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cultural Note</label>
                <textarea
                  value={editingAiEntry.data.cultural_note || ''}
                  onChange={(e) => updateEditingField('cultural_note', e.target.value)}
                  placeholder="Cultural context, when to use this word..."
                  rows={2}
                  className={`w-full rounded-lg p-2.5 border ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => setEditingAiEntry(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => saveDictGroqToVocabulary(editingAiEntry.word, editingAiEntry.data)}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
              >
                💾 Save to Vocabulary
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal - Full Featured */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2">
          <div className={`rounded-lg p-4 w-full border flex flex-col ${isDarkMode ? 'bg-gray-800 border-blue-600' : 'bg-white border-blue-400'}`} style={{height: '98vh', maxHeight: '98vh', maxWidth: '95vw'}}>
            <div className="flex justify-between items-center mb-2 flex-shrink-0">
              <h3 className="text-lg font-bold text-blue-500">📤 Bulk Upload Vocabulary</h3>
              <button onClick={() => { setShowBulkUploadModal(false); setBulkUploadText(''); setBulkUploadPreview([]); }} className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>×</button>
            </div>

            {/* Format Instructions — Collapsible */}
            <details className={`rounded-lg mb-2 text-sm flex-shrink-0 border ${isDarkMode ? 'bg-blue-900/50 border-blue-700' : 'bg-blue-50 border-blue-300'}`}>
              <summary className={`cursor-pointer px-4 py-2 font-medium select-none ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Format Guide & Template (click to expand)
              </summary>
              <div className="px-4 pb-3">
                <pre className={`text-xs p-2 rounded overflow-x-auto ${isDarkMode ? 'text-blue-200 bg-blue-950' : 'text-blue-800 bg-blue-100'}`}>
{`English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions like "f" (in)
Examples: Ana ghadi l-mṭar. → I am going to the airport.
Cultural Note: Airports in Morocco include Mohammed V (Casablanca)

English: hello
Darija: salam
Arabic: سلام
...`}
                </pre>
                <button
                  onClick={() => {
                    const template = `English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions like "f" (in) or "mn" (from).
Examples: Ana ghadi l-mṭar. → I am going to the airport.
Cultural Note: Airports in Morocco include Mohammed V (Casablanca) and Menara (Marrakech)

English: hello
Darija: salam
Arabic: سلام
Meaning: greeting
Pronunciation: sa-LAM
Type: Noun
Category: Greetings
Gender Note:
Grammar Note: very common greeting
Examples: Salam, kif dayr? → Hello, how are you?
Cultural Note:

English: thank you
Darija: shukran
Arabic: شكرا
Meaning: expression of gratitude
Pronunciation: SHUK-ran
Type: Expression
Category: Common
Gender Note:
Grammar Note: formal and informal
Examples: Shukran bzaaf! → Thank you very much!
Cultural Note: Moroccan culture emphasizes politeness and gratitude`;
                    const blob = new Blob([template], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'vocabulary_template.txt';
                    a.click();
                  }}
                  className="mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                >
                  ⬇️ Download Template
                </button>
              </div>
            </details>

            {/* Main Content - Two Columns, takes ALL remaining space */}
            <div className="flex gap-3 flex-1 overflow-hidden min-h-0">
              {/* Left - Input */}
              <div className="flex-1 flex flex-col min-h-0" style={{minWidth: '55%'}}>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Paste your TXT content here:</label>
                <textarea
                  className={`flex-1 rounded-lg p-4 border font-mono text-sm overflow-auto ${isDarkMode ? 'bg-gray-900 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'}`}
                  value={bulkUploadText}
                  placeholder={`English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions
Examples: Ana ghadi l-mṭar.

English: hello
Darija: salam
...`}
                  onChange={(e) => {
                    const text = e.target.value;
                    setBulkUploadText(text);

                    // Parse and update preview
                    const allLines = text.split('\n');
                    const words = [];
                    let currentWord = {};
                    let currentKey = '';

                    const keyMap = {
                      'english': 'english', 'darija': 'darija', 'darija latin': 'darija',
                      'arabic': 'arabic', 'arabic script': 'arabic',
                      'meaning': 'meaning', 'pronunciation': 'pronunciation',
                      'type': 'type', 'word type': 'type',
                      'category': 'category',
                      'gender note': 'genderNote', 'gendernote': 'genderNote',
                      'grammar note': 'grammarNote', 'grammarnote': 'grammarNote',
                      'cultural note': 'culturalNote', 'culturalnote': 'culturalNote',
                      'audio url': 'audioUrl', 'audiourl': 'audioUrl',
                      'examples': 'examples',
                    };

                    allLines.forEach((line) => {
                      const trimmedLine = line.trim();

                      const colonIndex = trimmedLine.indexOf(':');
                      if (colonIndex > 0 && colonIndex < 20) {
                        const key = trimmedLine.substring(0, colonIndex).trim();
                        const normalizedKey = keyMap[key.toLowerCase().replace(/[()]/g, '').trim()];

                        // Only recognize KNOWN field keys — unknown keys (Present:, Past:, Negative:, etc.) are treated as continuation text
                        if (normalizedKey) {
                          const value = trimmedLine.substring(colonIndex + 1).trim();

                          if (normalizedKey === 'english' && (currentWord.english || currentWord.darija)) {
                            words.push(currentWord);
                            currentWord = {};
                          }

                          currentWord[normalizedKey] = value;
                          currentKey = normalizedKey;
                        } else if (trimmedLine && currentKey) {
                          // Unknown key like "Present:" — append to current field
                          currentWord[currentKey] = (currentWord[currentKey] || '') + ' ' + trimmedLine;
                        }
                      } else if (trimmedLine && currentKey) {
                        currentWord[currentKey] = (currentWord[currentKey] || '') + ' ' + trimmedLine;
                      }
                    });

                    if (currentWord.english || currentWord.darija) {
                      words.push(currentWord);
                    }

                    setBulkUploadPreview(words);
                  }}
                />
              </div>

              {/* Right - Preview */}
              <div className="flex-1 flex flex-col min-h-0">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Preview ({bulkUploadPreview.length} words found):</label>
                <div className={`flex-1 rounded-lg p-3 border overflow-y-auto text-xs ${isDarkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  {bulkUploadPreview.length === 0 ? (
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Paste content on the left to see preview</p>
                  ) : (
                    <div className="space-y-2">
                      {bulkUploadPreview.map((word, idx) => (
                        <div key={idx} className={`p-2 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-green-500 font-medium">{word.darija || '?'}</span>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>#{idx + 1}</span>
                          </div>
                          <div className={isDarkMode ? 'text-white' : 'text-gray-800'}>{word.english || '?'}</div>
                          {word.arabic && <div className="text-blue-400 text-right" dir="rtl">{word.arabic}</div>}
                          {word.meaning && <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{word.meaning}</div>}
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {word.category && <span className="text-xs bg-purple-900 px-1 rounded text-purple-300">{word.category}</span>}
                            {word.type && <span className="text-xs bg-blue-900 px-1 rounded text-blue-300">{word.type}</span>}
                          </div>
                          {word.examples && <div className="text-yellow-500 text-xs mt-1 italic">📝 {word.examples}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className={`flex gap-3 justify-end mt-4 pt-4 border-t flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => { setShowBulkUploadModal(false); setBulkUploadText(''); setBulkUploadPreview([]); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                disabled={bulkUploadPreview.length === 0}
                onClick={async () => {
                  if (bulkUploadPreview.length === 0) {
                    showNotification('No words to upload', 'error');
                    return;
                  }

                  let successCount = 0;
                  for (const word of bulkUploadPreview) {
                    try {
                      const response = await fetch(`${API}/conversation-challenge/vocabulary`, {
                        method: 'POST',
                        headers: { ...authHeaders, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          darija: word.darija || '',
                          english: word.english || '',
                          arabic: word.arabic || '',
                          meaning: word.meaning || '',
                          pronunciation: word.pronunciation || '',
                          type: word.type || '',
                          category: word.category || 'Other',
                          genderNote: word.genderNote || '',
                          grammarNote: word.grammarNote || '',
                          culturalNote: word.culturalNote || '',
                          audioUrl: word.audioUrl || '',
                          examples: word.examples || '',
                        })
                      });
                      if (response.ok) successCount++;
                    } catch (e) {}
                  }

                  showNotification(`Uploaded ${successCount} of ${bulkUploadPreview.length} words`, 'success');
                  setShowBulkUploadModal(false);
                  setBulkUploadText('');
                  setBulkUploadPreview([]);
                  loadDictSavedVocabulary();
                }}
                className={`px-6 py-2 text-white rounded font-medium text-lg ${bulkUploadPreview.length > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 cursor-not-allowed'}`}
              >
                Import {bulkUploadPreview.length} Words
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DictionaryAdmin;
